'use client';
import { useState, useEffect } from 'react';
import { Card, Row, Col, Spinner, Table, Badge, Button, Form, Modal, Tabs, Tab, Alert } from 'react-bootstrap';
import { BarChart, Bar, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer, LineChart, Line, CartesianGrid } from 'recharts';
import Cookies from 'js-cookie';
import { useRouter } from 'next/navigation';
import { API_BASE_URL } from '@/config';

export default function AdminDashboard() {
    const [stats, setStats] = useState(null);
    const [prescriptions, setPrescriptions] = useState([]);
    const [clients, setClients] = useState([]);
    const [loading, setLoading] = useState(true);
    const [reportData, setReportData] = useState(null);
    const [newClientName, setNewClientName] = useState('');
    const [showGCModal, setShowGCModal] = useState(false);
    const [gcDays, setGcDays] = useState(0);
    const [filterClient, setFilterClient] = useState('all');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const router = useRouter();

    const fetchAllData = async () => {
        const token = Cookies.get('adminToken');
        if (!token) return router.push('/admin/login');

        try {
            const headers = { 'Authorization': `Bearer ${token}` };
            const [statsRes, presRes, clientRes, reportRes] = await Promise.all([
                fetch(`${API_BASE_URL}/api/v1/prescriptions/stats`, { headers }),
                fetch(`${API_BASE_URL}/api/v1/prescriptions/?client=${filterClient}&startDate=${startDate}&endDate=${endDate}&search=${searchTerm}`, { headers }),
                fetch(`${API_BASE_URL}/api/v1/clients/`, { headers }),
                fetch(`${API_BASE_URL}/api/v1/prescriptions/reports`, { headers })
            ]);

            const statsData = await statsRes.json();
            const presData = await presRes.json();
            const clientData = await clientRes.json();
            const reportData = await reportRes.json();

            setStats(statsData);
            setPrescriptions(presData.data || []);
            setClients(clientData.clients || []);
            setReportData(reportData);
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchAllData();
    }, [filterClient, startDate, endDate, searchTerm]);

    const handleCreateClient = async (e) => {
        e.preventDefault();
        const token = Cookies.get('adminToken');
        try {
            const res = await fetch(`${API_BASE_URL}/api/v1/clients/`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ name: newClientName })
            });
            if (res.ok) {
                setNewClientName('');
                fetchAllData();
                alert('Client created successfully!');
            } else {
                const errData = await res.json();
                alert(`Error: ${errData.error || 'Unknown error'}`);
            }
        } catch (err) { alert('Failed to create client'); }
    };

    const handleRegenerateApiKey = async (id) => {
        if (!confirm('Are you sure? The old key will stop working immediately.')) return;
        const token = Cookies.get('adminToken');
        try {
            const res = await fetch(`${API_BASE_URL}/api/v1/clients/${id}/regenerate`, {
                method: 'PUT',
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (res.ok) fetchAllData();
        } catch (err) { alert('Regeneration failed'); }
    };

    const handleDeleteClient = async (id) => {
        if (!confirm('Delete this client and all their API access?')) return;
        const token = Cookies.get('adminToken');
        try {
            const res = await fetch(`${API_BASE_URL}/api/v1/clients/${id}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (res.ok) {
                fetchAllData();
            } else {
                alert('Failed to delete client. Access denied or server error.');
            }
        } catch (err) { alert('Delete failed'); }
    };

    const handleDeletePrescription = async (id) => {
        if (!confirm('Are you sure you want to delete this log?')) return;
        const token = Cookies.get('adminToken');
        try {
            const res = await fetch(`${API_BASE_URL}/api/v1/prescriptions/${id}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (res.ok) {
                fetchAllData();
            } else {
                alert('Could not delete prescription. Permission denied.');
            }
        } catch (err) { alert('Delete failed'); }
    };

    const runGarbageCollection = async () => {
        const token = Cookies.get('adminToken');
        try {
            const res = await fetch(`${API_BASE_URL}/api/v1/prescriptions/gc?days=${gcDays}`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const result = await res.json();
            alert(result.message);
            setShowGCModal(false);
            fetchAllData();
        } catch (err) { alert('GC failed'); }
    };

    if (loading) return <div className="p-5 text-center"><Spinner animation="border" /></div>;

    const barData = [
        { name: 'Avg Backend Timing (ms)', STT: stats?.metrics?.avgSttTime || 0, LLM: stats?.metrics?.avgLlmTime || 0 }
    ];

    return (
        <div className="pt-4">
            <div className="d-flex justify-content-between align-items-center mb-4 pb-2 border-bottom">
                <h3 className="fw-bold">Clinical Admin Control Center</h3>
                <div className="d-flex gap-2">
                    <Button variant="outline-warning" size="sm" onClick={() => { setGcDays(0); setShowGCModal(true); }}>🧹 Garbage Clean</Button>
                    <Button variant="danger" size="sm" onClick={() => { Cookies.remove('adminToken'); router.push('/admin/login'); }}>Logout</Button>
                </div>
            </div>

            <Tabs defaultActiveKey="analytics" className="mb-4">
                <Tab eventKey="analytics" title="📊 Usage Analytics">
                    <Row className="g-4 mb-5">
                        <Col md={3}>
                            <Card className="shadow-sm border-0 bg-primary text-white text-center p-3 h-100">
                                <h1 className="fw-bold">{stats?.totalPrescriptions}</h1>
                                <small>TOTAL PROCESSED</small>
                            </Card>
                        </Col>
                        <Col md={9}>
                            <Card className="shadow-sm border-0 p-3 h-100">
                                <h6 className="fw-bold text-muted mb-3">PROCESSING LATENCY (MS)</h6>
                                <div style={{ height: 180 }}>
                                    <ResponsiveContainer>
                                        <BarChart data={barData}>
                                            <XAxis dataKey="name" />
                                            <YAxis />
                                            <Tooltip />
                                            <Bar dataKey="STT" fill="#0d6efd" barSize={40} />
                                            <Bar dataKey="LLM" fill="#198754" barSize={40} />
                                        </BarChart>
                                    </ResponsiveContainer>
                                </div>
                            </Card>
                        </Col>
                        <Col md={12}>
                            <Card className="shadow-sm border-0 p-3">
                                <h6 className="fw-bold text-muted mb-3">7-DAY VOLUME TREND</h6>
                                <div style={{ height: 250 }}>
                                    <ResponsiveContainer>
                                        <LineChart data={reportData?.dailyVolume || []}>
                                            <CartesianGrid strokeDasharray="3 3" vertical={false} />
                                            <XAxis dataKey="_id" />
                                            <YAxis />
                                            <Tooltip />
                                            <Line type="monotone" dataKey="count" stroke="#0d6efd" strokeWidth={3} />
                                        </LineChart>
                                    </ResponsiveContainer>
                                </div>
                            </Card>
                        </Col>
                    </Row>
                </Tab>

                <Tab eventKey="clients" title="🔑 Client API Keys">
                    <Card className="shadow-sm border-0 p-4 mb-4">
                        <h5 className="fw-bold mb-4">Manage API Clients</h5>
                        <Form className="d-flex gap-2 mb-4" onSubmit={handleCreateClient}>
                            <Form.Control
                                placeholder="Enter Client Name (e.g. Apollo Hospital)"
                                value={newClientName}
                                onChange={(e) => setNewClientName(e.target.value)}
                                required
                            />
                            <Button type="submit" variant="success">Generate API Key</Button>
                        </Form>
                        <Table responsive hover>
                            <thead className="table-light">
                                <tr>
                                    <th>Client Name</th>
                                    <th>API Key (cvp_...)</th>
                                    <th>Created</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {clients.map(c => (
                                    <tr key={c._id} className="align-middle">
                                        <td className="fw-bold">{c.name}</td>
                                        <td>
                                            <code>{c.apiKey}</code>
                                            <Badge bg="success" className="ms-2">Active</Badge>
                                        </td>
                                        <td>{new Date(c.createdAt).toLocaleDateString()}</td>
                                        <td>
                                            <div className="d-flex gap-2">
                                                <Button variant="outline-primary" size="sm" onClick={() => handleRegenerateApiKey(c._id)}>🔄 Regen Key</Button>
                                                <Button variant="outline-danger" size="sm" onClick={() => handleDeleteClient(c._id)}>🗑️ Delete</Button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                                {clients.length === 0 && <tr><td colSpan="4" className="text-center py-4 text-muted">No API clients registered.</td></tr>}
                            </tbody>
                        </Table>
                    </Card>
                </Tab>

                <Tab eventKey="logs" title="📋 Prescription Logs">
                    <Card className="shadow-sm border-0 overflow-hidden">
                        <Card.Header className="bg-white py-3 border-0">
                            <Row className="g-3">
                                <Col md={3}>
                                    <Form.Label className="small fw-bold text-muted uppercase mb-1">Filter by Client</Form.Label>
                                    <Form.Select
                                        size="sm"
                                        value={filterClient}
                                        onChange={(e) => setFilterClient(e.target.value)}
                                        className="rounded-3 shadow-sm"
                                    >
                                        <option value="all">🏢 All Clients / Direct</option>
                                        {clients.map(c => <option key={c._id} value={c._id}>{c.name}</option>)}
                                    </Form.Select>
                                </Col>
                                <Col md={2}>
                                    <Form.Label className="small fw-bold text-muted uppercase mb-1">Start Date</Form.Label>
                                    <Form.Control
                                        type="date"
                                        size="sm"
                                        className="rounded-3 shadow-sm"
                                        value={startDate}
                                        onChange={(e) => setStartDate(e.target.value)}
                                    />
                                </Col>
                                <Col md={2}>
                                    <Form.Label className="small fw-bold text-muted uppercase mb-1">End Date</Form.Label>
                                    <Form.Control
                                        type="date"
                                        size="sm"
                                        className="rounded-3 shadow-sm"
                                        value={endDate}
                                        onChange={(e) => setEndDate(e.target.value)}
                                    />
                                </Col>
                                <Col md={5}>
                                    <Form.Label className="small fw-bold text-muted uppercase mb-1">Keyword Search</Form.Label>
                                    <Form.Control
                                        size="sm"
                                        placeholder="🔍 Search medicines, symptoms..."
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                        className="rounded-3 shadow-sm"
                                    />
                                </Col>
                            </Row>
                        </Card.Header>
                        <Table responsive hover className="mb-0 text-center">
                            <thead className="table-light">
                                <tr>
                                    <th>Date</th>
                                    <th>Client / Source</th>
                                    <th>Original Text (Snippet)</th>
                                    <th>Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {prescriptions.map(p => (
                                    <tr key={p._id} className="align-middle">
                                        <td><div className="small">{new Date(p.createdAt).toLocaleDateString()}</div><div className="text-muted" style={{ fontSize: '0.75rem' }}>{new Date(p.createdAt).toLocaleTimeString()}</div></td>
                                        <td>
                                            {p.clientId ? <Badge bg="primary">{p.clientId.name}</Badge> : <Badge bg="light" text="dark border">Direct Upload</Badge>}
                                        </td>
                                        <td className="text-truncate" style={{ maxWidth: '400px' }}>{p.originalText}</td>
                                        <td>
                                            <Button variant="outline-danger" size="sm" className="px-3" onClick={() => handleDeletePrescription(p._id)}>Delete</Button>
                                        </td>
                                    </tr>
                                ))}
                                {prescriptions.length === 0 && (
                                    <tr>
                                        <td colSpan="4" className="py-5 text-muted fst-italic">No prescriptions match your selected filters.</td>
                                    </tr>
                                )}
                            </tbody>
                        </Table>
                    </Card>
                </Tab>

                <Tab eventKey="docs" title="📖 API Documentation">
                    <Card id="printable-docs" className="shadow-sm border-0 p-5">
                        <div className="d-flex justify-content-between align-items-center mb-4 pb-2 border-bottom d-print-none">
                            <h4 className="fw-bold mb-0">Developer Documentation</h4>
                            <Button variant="outline-dark" size="sm" onClick={() => window.print()}>📥 Export as PDF</Button>
                        </div>

                        <div className="docs-content">
                            <h2 className="fw-bold text-primary mb-3">Clinical Voice-to-Prescription API</h2>
                            <p className="text-muted mb-4 lead">Integration guide for third-party hospitals and clinics.</p>

                            <section className="mb-5">
                                <h5 className="fw-bold text-uppercase small text-muted mb-3 border-bottom pb-2">1. Authentication</h5>
                                <p>To authenticate your requests, include your secret API key in the <code>X-API-Key</code> header of all requests.</p>
                                <Card className="bg-light border-0 p-3 mb-3">
                                    <code>Header: X-API-Key: cvp_your_unique_key_here</code>
                                </Card>
                                <Alert variant="info" className="small">
                                    You can generate and manage keys in the <strong>Client API Keys</strong> tab of this dashboard.
                                </Alert>
                            </section>

                            <section className="mb-5">
                                <h5 className="fw-bold text-uppercase small text-muted mb-3 border-bottom pb-2">2. Processing Audio (POST)</h5>
                                <p>Main endpoint to upload a Dr-Patient consultation and get a structured, speaker-differentiated prescription.</p>
                                <div className="mb-3">
                                    <strong>Endpoint:</strong> <Badge bg="dark" className="ms-2">POST /api/v1/prescriptions/process</Badge>
                                </div>
                                <Table striped bordered hover size="sm" className="small mb-4">
                                    <thead className="table-light">
                                        <tr>
                                            <th>Param</th>
                                            <th>Type</th>
                                            <th>Description</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr>
                                            <td><code>audio</code></td>
                                            <td>File</td>
                                            <td>Consultation recording (wav, mp3, m4a). Max 25MB.</td>
                                        </tr>
                                        <tr>
                                            <td><code>language</code></td>
                                            <td>String</td>
                                            <td>Set to <code>'auto'</code> for mixing languages (default).</td>
                                        </tr>
                                    </tbody>
                                </Table>

                                <h6 className="fw-bold small mb-2">Example JavaScript (Fetch):</h6>
                                <pre className="p-3 bg-dark text-white rounded-3 small overflow-auto">
                                    {`const formData = new FormData();
formData.append('audio', fileInput.files[0]);

fetch(API_BASE_URL + '/api/v1/prescriptions/process', {
  method: 'POST',
  headers: { 'X-API-Key': 'cvp_...' },
  body: formData
})
.then(res => res.json())
.then(data => console.log(data));`}
                                </pre>
                            </section>

                            <section className="mb-5">
                                <h5 className="fw-bold text-uppercase small text-muted mb-3 border-bottom pb-2">3. Response Structure (JSON)</h5>
                                <p>The API returns a structured object with mapped medical fields and a dialogue transcript.</p>
                                <pre className="p-3 bg-light text-dark border rounded-3 small overflow-auto">
                                    {`{
  "success": true,
  "data": {
    "originalText": "Doctor: How are you? Patient: I have a cough...",
    "language": "english",
    "structuredData": {
      "chiefComplaintsAndPastHistory": "Dry cough for 3 days",
      "diagnosisAndKnownDrugAllergy": "Acute Bronchitis",
      "medicinePrescribed": "Syrup Ascoril 10ml TDS",
      ...
    },
    "dialogue": [
      { "speaker": "Doctor", "text": "How are you?" },
      { "speaker": "Patient", "text": "I have a cough..." }
    ]
  }
}`}
                                </pre>
                            </section>

                            <section className="mb-4 text-center mt-5 pt-5 d-none d-print-block">
                                <hr />
                                <small className="text-muted">Generated by Clinical AI Control Center | Confidential Documentation</small>
                            </section>
                        </div>
                    </Card>

                    <style dangerouslySetInnerHTML={{
                        __html: `
                        @media print {
                            body * { visibility: hidden; }
                            #printable-docs, #printable-docs * { visibility: visible; }
                            #printable-docs {
                                position: absolute;
                                left: 0;
                                top: 0;
                                width: 100%;
                                background: white !important;
                                box-shadow: none !important;
                                border: none !important;
                                padding: 20px !important;
                            }
                            .d-print-none { display: none !important; }
                            .d-print-block { display: block !important; }
                            pre {
                                border: 1px solid #ddd !important;
                                white-space: pre-wrap !important;
                                background-color: #f8f9fa !important;
                                color: black !important;
                                font-family: monospace;
                            }
                            .docs-content { color: black !important; }
                        }
                    ` }} />
                </Tab>
            </Tabs>

            <Modal show={showGCModal} onHide={() => setShowGCModal(false)}>
                <Modal.Header closeButton><Modal.Title>Database Garbage Collection</Modal.Title></Modal.Header>
                <Modal.Body>
                    <p className="fw-bold">This utility helps manage database size by removing old records.</p>
                    <Form.Group>
                        <Form.Label>Select logic:</Form.Label>
                        <Form.Select value={gcDays} onChange={(e) => setGcDays(e.target.value)}>
                            <option value="0">🚨 DELETE EVERYTHING (Clear All Data)</option>
                            <option value="1">📅 Older than 24 Hours</option>
                            <option value="7">📅 Older than 7 Days</option>
                            <option value="30">📅 Older than 30 Days</option>
                        </Form.Select>
                    </Form.Group>
                    <Alert variant="warning" className="mt-3 small py-2">
                        Note: "Older than X Days" will NOT delete records from today. Use "Delete Everything" to wipe the entire log.
                    </Alert>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowGCModal(false)}>Cancel</Button>
                    <Button variant="warning" onClick={runGarbageCollection}>Start Cleanup</Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
}
